/*
 * king-personalisation.js
 * -------------------------------------------------------------------------
 * Resolves the King ("ymq") Product Options assignment LIVE, for any product,
 * on any page — and renders the resulting "Name to be printed" style fields
 * into the cart AND into the add-to-cart popup.
 *
 * WHY THIS REPLACES THE OLD SNAPSHOT
 * -------------------------------------------------------------------------
 * snippets/personalisation-fields.liquid used to carry `king_config`: a
 * hand-pasted SNAPSHOT of King's collection-ID / product-ID assignment, kept
 * in sync by a script (scripts/sync-king-personalisation.js) that no longer
 * exists in the repo. It drifted. By 2026-08 King had 11 templates and the
 * snapshot had 8 — tem11/tem12 ("Name to be printed - Bag Charm") were missing
 * entirely, so Bag Charms and Hair Clips showed a REQUIRED field on the
 * product page and nothing at all in the cart or the popup.
 *
 * The fix is to stop copying King and start reading it.
 *
 * WHERE THE CONFIG COMES FROM
 * -------------------------------------------------------------------------
 * King's app-embed server-renders its ENTIRE config into every PRODUCT page,
 * one line per template:
 *
 *     best_option.ymq_template_options[`tem11`] = {"template":{...},"assign":{...}};
 *
 * That is the whole store's config — all templates, all assignment rules — not
 * just the current product's. So ONE product page is a complete source.
 *
 * Neither the cart NOR the home page carries any of it (verified: `best_option`
 * is undefined on /cart), which is the constraint that produced the snapshot in
 * the first place. So we bridge it:
 *
 *   1. Every product page harvests `best_option` into localStorage.
 *   2. The cart and the popup read that cache.
 *   3. Cold cache (shopper lands on the home page, adds from a card, and has
 *      never opened a product page) → fetch any product URL and parse the
 *      config out of its HTML.
 *
 * Net effect: a new product, a new collection, a brand-new King template all
 * take effect immediately. There is nothing left to re-sync and no theme
 * deploy needed when the King admin changes.
 * -------------------------------------------------------------------------
 */
(function () {
  var LS_KEY = 'zn_king_cfg_v1';
  var MAX_AGE_MS = 12 * 60 * 60 * 1000; // refetch at most twice a day per shopper
  var NS = (window.ZNKing = window.ZNKing || {});
  // Included from both the cart-notification snippet and buy-buttons.liquid;
  // the second copy must be a no-op (one observer, one notify()).
  if (NS.__loaded) return;
  NS.__loaded = true;

  // ---- "Please enter name to be printed" popup ----------------------------
  // Shared by cart-personalisation.js (cart page) and
  // cart-notification-personalisation.js (add-to-cart popup): both scripts
  // used to DISABLE the checkout button while a required name was empty, which
  // shoppers read as "checkout is broken". Since 1.3.1 the button stays live;
  // a click with a missing name shows this popup and drops the cursor into the
  // first empty field instead. Non-modal on purpose — focus must land in the
  // input, so nothing here may steal it. Dismisses on typing, on its own
  // close button, or after a few seconds.
  var ALERT_ID = 'zn-cp-alert';
  var alertTimer = null;
  NS.notify = function (msg, input) {
    var old = document.getElementById(ALERT_ID);
    if (old) old.remove();
    if (alertTimer) { clearTimeout(alertTimer); alertTimer = null; }

    var box = document.createElement('div');
    box.id = ALERT_ID;
    box.className = 'zn-cp-alert';
    box.setAttribute('role', 'alert');
    box.setAttribute('aria-live', 'assertive');

    var icon = document.createElement('span');
    icon.className = 'zn-cp-alert__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.appendChild(document.createTextNode('!'));
    box.appendChild(icon);

    var text = document.createElement('span');
    text.className = 'zn-cp-alert__text';
    text.appendChild(document.createTextNode(msg));
    box.appendChild(text);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'zn-cp-alert__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.appendChild(document.createTextNode('×'));
    close.addEventListener('click', function () { NS.dismiss(); if (input) input.focus(); });
    box.appendChild(close);

    document.body.appendChild(box);
    // Next frame so the CSS transition runs from the hidden state.
    requestAnimationFrame(function () { box.classList.add('zn-cp-alert--show'); });
    alertTimer = setTimeout(NS.dismiss, 6000);

    if (input) {
      try { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      // Focus AFTER the popup is in the DOM so a screen reader announces the
      // alert first, then the field it refers to. preventScroll keeps the
      // smooth scroll above from being cut short.
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      var once = function () { NS.dismiss(); input.removeEventListener('input', once); };
      input.addEventListener('input', once);
    }
  };
  NS.dismiss = function () {
    if (alertTimer) { clearTimeout(alertTimer); alertTimer = null; }
    var box = document.getElementById(ALERT_ID);
    if (!box) return;
    box.classList.remove('zn-cp-alert--show');
    setTimeout(function () { if (box.parentNode) box.remove(); }, 250);
  };

  // ---- config normalisation ----------------------------------------------
  // Two shapes exist on a product page and they carry the same data:
  //   ymq_template_options      = { temN: { template: {...}, assign: {...} } }
  //   ymq_option_template_c_t   = { temN: {...} }   (+ ..._assign_c_t alongside)
  // The first is what the app writes; the others are derived from it. Prefer
  // the first, fall back to recombining the second pair.
  function fromBestOption(bo) {
    if (!bo) return null;
    var out = {};

    var to = bo.ymq_template_options;
    if (to) {
      Object.keys(to).forEach(function (k) {
        var rec = to[k];
        if (rec && rec.template && rec.assign) out[k] = { template: rec.template, assign: rec.assign };
      });
      if (Object.keys(out).length) return out;
    }

    var t = bo.ymq_option_template_c_t;
    var a = bo.ymq_option_template_assign_c_t;
    if (t && a) {
      Object.keys(t).forEach(function (k) {
        if (a[k]) out[k] = { template: t[k], assign: a[k] };
      });
    }
    return Object.keys(out).length ? out : null;
  }

  // One template per line in the server-rendered HTML, so a line-scoped regex
  // is reliable — no brace balancing needed.
  var LINE_RE = /ymq_template_options\[\s*[`'"](tem\d+)[`'"]\s*\]\s*=\s*(\{[\s\S]*\});\s*$/;

  function parseFromHtml(html) {
    var out = {};
    String(html || '').split('\n').forEach(function (line) {
      var m = LINE_RE.exec(line.trim());
      if (!m) return;
      try {
        var rec = JSON.parse(m[2]);
        if (rec && rec.template && rec.assign) out[m[1]] = { template: rec.template, assign: rec.assign };
      } catch (e) { /* a template we can't parse is skipped, not fatal */ }
    });
    return Object.keys(out).length ? out : null;
  }

  // ---- cache --------------------------------------------------------------
  function readCache() {
    try {
      var rec = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (!rec || !rec.cfg || !rec.ts) return null;
      if (Date.now() - rec.ts > MAX_AGE_MS) return null;
      return rec.cfg;
    } catch (e) { return null; }
  }

  function writeCache(cfg) {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ ts: Date.now(), cfg: cfg })); } catch (e) {}
  }

  // ---- matching -----------------------------------------------------------
  function csvSet(s) {
    var out = {};
    String(s == null ? '' : s).split(',').forEach(function (x) {
      x = x.trim();
      if (x) out[x] = 1;
    });
    return out;
  }

  // Mirrors King's own manual assignment: a template applies to a product if
  // the product id, ANY of its collection ids, or ANY of its tags is listed.
  //
  // King also supports "automate" rules (assign.type != 0). Those are evaluated
  // by the app itself and are not reproducible here, so such a template is
  // SKIPPED rather than guessed at — a guess that matched wrongly would block
  // checkout on a product that needs no name. All 11 templates in use as of
  // 2026-08 are manual (type 0). If an automate rule is ever added, the flag
  // below records it so it is visible rather than silent.
  function matches(assign, ctx) {
    if (!assign) return false;
    if (Number(assign.type) !== 0) {
      NS.unsupportedAssignment = true;
      return false;
    }
    var man = assign.manual || {};

    if (csvSet(man.product)[String(ctx.productId)]) return true;

    var cols = csvSet(man.collection);
    for (var i = 0; i < ctx.collectionIds.length; i++) {
      if (cols[String(ctx.collectionIds[i])]) return true;
    }

    var tags = csvSet(man.tag);
    for (var j = 0; j < ctx.tags.length; j++) {
      if (tags[String(ctx.tags[j])]) return true;
    }
    return false;
  }

  // King field types: 1 = single-line text, 2 = textarea. Everything else
  // (select, swatch, file upload, …) is the app's own UI with its own pricing
  // and conditional logic; the cart deliberately does NOT re-create it. The
  // cart's only job is to re-collect the free-text personalisation that the
  // instant-add path drops. No non-text field is currently required, so
  // skipping them cannot let a required value through.
  function fieldsOf(rec) {
    var out = [];
    var tpl = rec.template || {};
    Object.keys(tpl).forEach(function (fid) {
      var f = tpl[fid] || {};
      var type = String(f.type);
      if (type !== '1' && type !== '2') return;
      var label = String(f.label == null ? '' : f.label);
      if (!label) return;
      out.push({
        label: label,
        required: Number(f.required) === 1,
        min: parseInt(f.min_char, 10) || 0,
        max: parseInt(f.max_char, 10) || 0,
        multiline: type === '2',
        placeholder: String(f.placeholder || ''),
        help: String(f.help || '')
      });
    });
    return out;
  }

  function temNum(k) { return parseInt(String(k).replace(/^tem/, ''), 10) || 0; }

  /**
   * Field specs that apply to one product.
   * ctx = { productId, collectionIds: [], tags: [] }
   *
   * Labels are deduped, because a product can legitimately match two templates
   * that ask for the same thing. When they disagree we keep the STRICTER rule
   * (shorter max, required wins) so the cart never accepts a value King's own
   * product-page form would have rejected.
   */
  NS.resolve = function (cfg, ctx) {
    var byLabel = {};
    var order = [];

    Object.keys(cfg || {}).sort(function (a, b) { return temNum(a) - temNum(b); }).forEach(function (k) {
      if (!matches(cfg[k].assign, ctx)) return;
      fieldsOf(cfg[k]).forEach(function (f) {
        var prev = byLabel[f.label];
        if (!prev) {
          byLabel[f.label] = f;
          order.push(f.label);
          return;
        }
        prev.required = prev.required || f.required;
        if (f.max && (!prev.max || f.max < prev.max)) prev.max = f.max;
        if (f.min > prev.min) prev.min = f.min;
      });
    });

    return order.map(function (l) { return byLabel[l]; });
  };

  // ---- acquisition --------------------------------------------------------
  // `best_option` is written by an inline script, but King's own bundle can
  // finish populating it a moment later, so poll briefly instead of reading once.
  function harvestLocal(cb) {
    var tries = 0;
    (function tick() {
      var cfg = fromBestOption(window.best_option);
      if (cfg) { writeCache(cfg); return cb(cfg); }
      if (++tries > 40) return cb(null); // ~6s
      setTimeout(tick, 150);
    })();
  }

  // Retried, because a single failure here fails OPEN: no config means no
  // field is rendered and the popup's checkout is never gated. Shopify answers
  // 429 under load (and returns a plain error page, not the product HTML), and
  // a shopper on a flaky connection is exactly the case that matters. Three
  // attempts with backoff; a non-2xx response is a failure, not HTML to parse.
  var FETCH_DELAYS = [0, 600, 2000];

  function fetchConfig(seedUrl, cb) {
    if (!seedUrl) return cb(null);

    function attempt(n) {
      fetch(seedUrl, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var cfg = parseFromHtml(html);
          if (cfg) { writeCache(cfg); return cb(cfg); }
          throw new Error('no config in response');
        })
        .catch(function () {
          if (n + 1 < FETCH_DELAYS.length) {
            setTimeout(function () { attempt(n + 1); }, FETCH_DELAYS[n + 1]);
          } else {
            cb(null);
          }
        });
    }

    attempt(0);
  }

  /**
   * Resolve the store's King config from the cheapest source available.
   * seedUrl is any product URL, used only when there is nothing cached and the
   * current page is not itself a product page.
   */
  NS.load = function (seedUrl, cb) {
    if (window.best_option) {
      return harvestLocal(function (cfg) {
        if (cfg) return cb(cfg);
        var cached = readCache();
        cached ? cb(cached) : fetchConfig(seedUrl, cb);
      });
    }
    var cached = readCache();
    if (cached) return cb(cached);
    fetchConfig(seedUrl, cb);
  };

  // ---- rendering ----------------------------------------------------------
  // Builds the same markup snippets/personalisation-fields.liquid used to emit
  // server-side, so assets/cart-personalise.css and the save/gate logic in
  // cart-personalisation.js and cart-notification-personalisation.js keep
  // working untouched. Built with DOM APIs rather than innerHTML: labels and
  // help text are merchant-authored strings from the King admin and must never
  // be parsed as markup.
  function buildField(slot, spec, existing, idx) {
    var wrap = document.createElement('div');
    wrap.className = 'cart-personalise' + (existing ? '' : ' cart-personalise--empty');
    wrap.setAttribute('data-cp', '');
    wrap.setAttribute('data-line-key', slot.getAttribute('data-line-key'));

    var fid = 'cp-' + slot.getAttribute('data-line-index') + '-' + idx;

    var label = document.createElement('label');
    label.className = 'cart-personalise__label';
    label.setAttribute('for', fid);
    label.appendChild(document.createTextNode(spec.label));
    if (spec.required) {
      var star = document.createElement('span');
      star.className = 'cart-personalise__req';
      star.setAttribute('aria-hidden', 'true');
      star.appendChild(document.createTextNode(' *'));
      label.appendChild(star);
    }
    wrap.appendChild(label);

    var input = document.createElement(spec.multiline ? 'textarea' : 'input');
    input.className = 'cart-personalise__input';
    input.id = fid;
    if (spec.multiline) input.rows = 2; else input.type = 'text';
    if (spec.max) input.setAttribute('maxlength', String(spec.max));
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('data-cp-input', '');
    input.setAttribute('data-cp-required', spec.required ? 'true' : 'false');
    if (spec.min) input.setAttribute('data-cp-min', String(spec.min));
    input.setAttribute('data-cp-label', spec.label);
    input.setAttribute('data-line-key', slot.getAttribute('data-line-key'));
    // King's hint goes in the PLACEHOLDER only, never as a value. Putting it in
    // the value is exactly the King admin misconfiguration (default_text) that
    // let Bag Charm orders through blank: a non-empty value satisfies every
    // required-check, and King then strips it again when building the line-item
    // properties.
    input.setAttribute('placeholder', spec.placeholder || 'Type the name to print…');
    input.value = existing || '';
    wrap.appendChild(input);

    if (spec.help) {
      var help = document.createElement('span');
      help.className = 'cart-personalise__help';
      help.appendChild(document.createTextNode(spec.help));
      wrap.appendChild(help);
    }

    if (spec.required) {
      var err = document.createElement('span');
      err.className = 'cart-personalise__error';
      err.setAttribute('data-cp-error', '');
      err.hidden = true;
      err.appendChild(document.createTextNode('Please add “' + spec.label.trim() + '” before checkout.'));
      wrap.appendChild(err);
    }

    return wrap;
  }

  function readJson(el, attr) {
    try { return JSON.parse(el.getAttribute(attr) || 'null'); } catch (e) { return null; }
  }

  // Resolved config for this page, kept so the add-to-cart popup can render
  // instantly on the second and subsequent adds.
  var CFG = null;

  function ensureCfg(seedUrl, cb) {
    if (CFG) return cb(CFG);
    NS.load(seedUrl, function (cfg) {
      if (cfg) CFG = cfg;
      cb(cfg);
    });
  }

  // Renders the slots handed to it. Defaults to the ones still PENDING, so a
  // re-render never wipes a name the shopper has typed but not yet blurred.
  function renderSlots(cfg, slots) {
    slots = slots || document.querySelectorAll('[data-cp-slot][data-cp-pending]');
    Array.prototype.forEach.call(slots, function (slot) {
      while (slot.firstChild) slot.removeChild(slot.firstChild);

      var specs = cfg
        ? NS.resolve(cfg, {
            productId: slot.getAttribute('data-product-id'),
            collectionIds: (slot.getAttribute('data-collections') || '').split(',').filter(Boolean),
            tags: (readJson(slot, 'data-tags') || [])
          })
        : [];

      var props = readJson(slot, 'data-props') || {};
      specs.forEach(function (spec, i) {
        slot.appendChild(buildField(slot, spec, props[spec.label], i + 1));
      });

      // The cart and the add-to-cart popup also print line-item properties
      // read-only. Any label we render an editable field for must be dropped
      // from that list or the shopper sees it twice. Both templates used to do
      // this with a hardcoded label list that drifted out of sync with King
      // exactly as king_config did — "Name to be printed - Bag Charm" was in
      // neither. It is derived here instead, from what was actually rendered.
      var row = slot.closest('tr') || slot.closest('.cart-item') || slot.parentNode;
      if (row) {
        var rendered = {};
        specs.forEach(function (s) { rendered[s.label] = 1; });
        Array.prototype.slice.call(row.querySelectorAll('[data-cp-prop]')).forEach(function (el) {
          if (rendered[el.getAttribute('data-cp-prop')]) el.remove();
        });
      }

      slot.removeAttribute('data-cp-pending');
    });

    document.dispatchEvent(new CustomEvent('zn:king-fields-rendered'));
  }

  // Fill in every slot that is still undecided, wherever it came from: the cart
  // page at load, or the add-to-cart popup injected later.
  function renderPending() {
    var pending = document.querySelectorAll('[data-cp-slot][data-cp-pending]');
    if (!pending.length) return;
    var seed = pending[0].getAttribute('data-cp-seed-url');
    ensureCfg(seed, function (cfg) {
      // A null cfg means King is unreachable after retries. The pending flag is
      // still cleared: leaving checkout permanently blocked because a fetch
      // failed would cost real orders.
      if (!cfg) NS.configUnavailable = true;
      renderSlots(cfg, pending);
    });
  }

  function init() {
    // On a product page there are no slots — we are here only to keep the cache
    // warm so the popup and the cart can render without a round trip.
    if (window.best_option) harvestLocal(function (cfg) { if (cfg) CFG = cfg; });
    renderPending();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // The add-to-cart popup injects its markup AFTER page load, and Dawn replaces
  // the cart contents wholesale on a quantity change — both produce fresh
  // pending slots. Watch for them rather than rendering only once at startup.
  var pendTimer = null;
  function schedulePending() {
    if (pendTimer) return;
    pendTimer = setTimeout(function () { pendTimer = null; renderPending(); }, 60);
  }

  try {
    new MutationObserver(schedulePending)
      .observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) { /* observer unsupported: the cart page still renders at init */ }

  document.addEventListener('zn:cart-rerendered', renderPending);
})();